import Vue from 'vue'
import Vuex from 'vuex'
import router from '@/router'
import { PLANNER_API_URL, SESSION_GROUP_ID } from '@/config'
import { addAttributes } from '@/lib/sessions'

Vue.use(Vuex)

import { schedule } from '@/store/schedule'

export default new Vuex.Store({

  modules: {
    schedule,
  },

  state: {
    device: 'desktop',
    width: 0,
    sidebar: true,
    day: 1,
    program: 'horizontal',
    sort: 'date',
    progress: false,
    settings: {},
    _: [],
    days: [],
    rooms: [],
    options: [],
    filter: {
      room: [],
      session_group: [],
      session_type: [],
    },
    infos: [],
    sessions: [],
    persons: [],
    search: '',
    results: [],
    favorites: {
      sessions: [],
      presentations: [],
    },
  },

  getters: {
    device: state => state.device,
    width: state => state.width,
    sidebar: state => state.sidebar,
    day: state => state.day,
    program: state => state.program,
    sort: state => state.sort,
    progress: state => state.progress,
    settings: state => state.settings,
    _: state => state._,
    days: state => state.days,
    rooms: state => state.rooms,
    options: state => state.options,
    filter: state => state.filter,
    infos: state => state.infos,
    sessions: state => state.sessions,
    persons: state => state.persons,
    search: state => state.search,
    results: state => state.results,
    favorites: state => state.favorites,
  },

  mutations: {
    setDevice(state, device) { state.device = device },
    setWidth(state, width) { state.width = width },
    setSidebar(state, sidebar) { state.sidebar = sidebar },
    setProgramDay(state, day) { 
      state.sessions = [],
      state.infos = [],
      state.rooms = [],
      state.day = day
    },
    setProgramMode(state, program) { state.program = program },
    setProgramSort(state, sort) { state.sort = sort },
    setProgress(state, progress) { state.progress = progress },
    setSettings(state, settings) { state.settings = settings },
    setWordings(state, wordings) { state._ = wordings },
    setDays(state, days) { state.days = days },
    setRooms(state, rooms) { state.rooms = rooms },
    setOptions(state, options) { state.options = options },
    updateFilter(state, filter) {
      const index = state.filter[filter.group].indexOf(filter.id);
      if (index >= 0) {
        state.filter[filter.group].splice(index, 1);
      } else {
        state.filter[filter.group].push(filter.id);
      }
    },
    resetFilter(state, filter) { state.filter[filter] = [] },
    setInfos(state, infos) { state.infos = infos },
    setSessions(state, sessions) {
      state.sessions = addAttributes(sessions);
    },
    setPersons(state, persons) { state.persons = persons },
    setSearch(state, search) { state.search = search },
    setResults(state, results) { state.results = results },
    setFavoriteSessions(state, sessions) { state.favorites.sessions = sessions },
    setFavoritePresentations(state, presentations) { state.favorites.presentations = presentations },
  },

  actions: {
    getSettings: () => Vue.axios.get(PLANNER_API_URL + '/settings'),
    getWordings: ({ state }, language) => Vue.axios.get(PLANNER_API_URL + '/wordings', {
      params: {
        language_id: language || state.settings.program.language,
      }
    }),
    getDays: () => Vue.axios.get(PLANNER_API_URL + '/program/days'),
    getOptions: () => Vue.axios.get(PLANNER_API_URL + '/program/options'),
    initializeProgram: async ({ commit, dispatch }) => {
      const settings = await dispatch('getSettings');
      commit('setSettings', settings.data);
      const [wordings, days, options] = await Promise.all([
        dispatch('getWordings'),
        dispatch('getDays'),
        dispatch('getOptions'),
      ]);
      commit('setWordings', wordings.data);
      commit('setDays', days.data);
      commit('setOptions', options.data);
      commit('setProgress', false);
    },
    changeLanguage: async ({ state, commit, dispatch }, language) => {
      state.settings.program.language = language;
      const wordings = await dispatch('getWordings', language);
      commit('setWordings', wordings.data);
      commit('setProgress', false);
    },
    getRooms: async ({ state, commit }, data) => {
      commit('setRooms', []);
      const id = state.days[(data.key - 1)].id;
      const rooms = await Vue.axios.get(PLANNER_API_URL + '/program/rooms/' + id);
      commit('setRooms', rooms.data);
      commit('setProgress', false);
    },
    getInfos: async ({ state, commit }, data) => {
      if (data.reset) commit('setInfos', []);
      const id = state.days[(data.key - 1)].id;
      const infos = await Vue.axios.get(PLANNER_API_URL + '/program/days/' + id, { params: {
        program_mode: state.program,
        filter_display_type: 8,
      }});
      commit('setInfos', infos.data);
      commit('setProgress', false);
    },
    getSessions: async ({ state, commit }, data) => {
      if (data.reset) commit('setSessions', []);
      const id = state.days[(data.key - 1)].id;
      const sessions = await Vue.axios.get(PLANNER_API_URL + '/program/days/' + id, { params: {
        program_mode: state.program,
        program_sort: state.sort,
        filter_session_group: state.filter.session_group,
        filter_session_type: state.filter.session_type,
        filter_room: state.filter.room,
        filter_display_type: '7,' + SESSION_GROUP_ID,
        filter_group: 'is_not_child',
      }});
      commit('setSessions', sessions.data);
      commit('setProgress', false);
    },
    getPersons: async ({ state, commit }) => {
      const persons = await Vue.axios.get(PLANNER_API_URL + '/program/persons', { params: {
        filter_session_group: state.filter.session_group,
        filter_session_type: state.filter.session_type,
        filter_room: state.filter.room,
      }});
      commit('setPersons', persons.data);
      commit('setProgress', false);
    },
    updateFilter: ({ commit, dispatch }, filter) => {
      commit('updateFilter', filter);
      if (router.currentRoute.path.match(/^\/program/)) {
        dispatch('getSessions', { key: router.currentRoute.params.key });
      }
      if (router.currentRoute.path.match(/^\/persons/)) {
        dispatch('getPersons');
      }
    },
    resetFilter: ({ commit, dispatch }, filter) => {
      commit('resetFilter', filter);
      if (router.currentRoute.path.match(/^\/program/)) {
        dispatch('getSessions', { key: router.currentRoute.params.key });
      }
      if (router.currentRoute.path.match(/^\/persons/)) {
        dispatch('getPersons');
      }
    },
    getResults: async ({ state, commit }) => {
      const results = await Vue.axios.get(PLANNER_API_URL + '/program/search', { params: {
        search: state.search,
      }});
      commit('setResults', results.data);
      commit('setProgress', false);
    },
  },
})